import React, { useState, useRef, useEffect } from 'react';
import { Edit, Trash2, MoreVertical, Plus } from 'lucide-react';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';
import { gamesApi } from '../../api/games';
import toast from 'react-hot-toast';
import EditDefaultGamePackage from '../../components/Popup/EditDefaultGamePackage';
import { getGameImage } from '../../utils/getGameImage';
import getGameType from '../../utils/games';

export default function DefaultGamePackageRow({
  package: gamePackage,
  gameSettingDetails,
  onDelete,
  refreshPackages,
  formatCurrency,
  isAdmin,
}) {
  const { tokens } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleDeleteConfirm = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(gamePackage.id);
      }
    });
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleAssignToAllPlayers = async () => {
    try {
      const loadingToastId = toast.loading(
        'Assigning package to all players...'
      );

      const response = await gamesApi.assignDefaultPackageToPlayers(
        tokens.access.token,
        gamePackage.id
      );

      toast.dismiss(loadingToastId);

      if (response.status === 200) {
        Swal.fire({
          title: 'Success!',
          text: `Package assigned to ${response.data.assignedCount} players`,
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });

        if (refreshPackages) {
          refreshPackages();
        }
      } else {
        toast.error(
          'Failed to assign package: ' +
            (response.data?.message || 'Unknown error')
        );
      }
    } catch (error) {
      console.error('Error assigning package to players:', error);
      toast.error(error.message || 'Failed to assign package to players');
    }
  };

  // Get appropriate image for game type
  let GameIcon = getGameType(gamePackage?.thumbnail?.url || 'default');
  GameIcon = getGameImage(GameIcon || 'default');

  return (
    <>
      <div className='bg-white rounded-lg shadow-sm border border-gray-100'>
        <div className='p-3'>
          {/* Header with game info and actions button */}
          <div className='flex items-center justify-between mb-2'>
            <div className='flex items-center gap-2'>
              {GameIcon && (
                <img src={GameIcon} className='w-5 h-5 flex-shrink-0' />
              )}
              <div>
                <p className='font-medium text-gray-900'>{gamePackage.name}</p>
                <p className='text-xs text-gray-500'>
                  {gameSettingDetails?.name || 'Unknown'}
                </p>
              </div>
            </div>

            {isAdmin && (
              <div ref={dropdownRef} className='relative'>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className='p-1.5 rounded-full hover:bg-gray-100'
                >
                  <MoreVertical className='w-5 h-5 text-gray-500' />
                </button>

                {dropdownOpen && (
                  <div className='absolute right-0 top-full mt-1 z-10 bg-white border border-gray-200 rounded-md shadow-lg w-36'>
                    <ul className='py-1 text-sm'>
                      <li
                        className='px-3 py-2 hover:bg-gray-100 flex items-center gap-2 cursor-pointer'
                        onClick={() => {
                          handleEdit();
                          setDropdownOpen(false);
                        }}
                      >
                        <Edit className='w-4 h-4 text-blue-500' />
                        Edit
                      </li>
                      <li
                        className='px-3 py-2 hover:bg-gray-100 flex items-center gap-2 cursor-pointer'
                        onClick={() => {
                          handleDeleteConfirm();
                          setDropdownOpen(false);
                        }}
                      >
                        <Trash2 className='w-4 h-4 text-red-500' />
                        Delete
                      </li>
                      <li
                        className='px-3 py-2 hover:bg-gray-100 flex items-center gap-2 cursor-pointer'
                        onClick={() => {
                          handleAssignToAllPlayers();
                          setDropdownOpen(false);
                        }}
                      >
                        <Plus className='w-4 h-4 text-green-500' />
                        Assign to All
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Game rates info */}
          <div className='grid grid-cols-4 gap-2 text-xs md:text-sm'>
            <div>
              <p className='text-gray-500'>Comm</p>
              <p className='font-medium'>
                {formatCurrency(gamePackage.commissionRate)}%
              </p>
            </div>
            <div>
              <p className='text-gray-500'>Win</p>
              <p className='font-medium'>
                ₱{formatCurrency(gamePackage.winRate)}
              </p>
            </div>
            <div>
              <p className='text-gray-500'>2x Win</p>
              <p className='font-medium'>
                ₱{formatCurrency(gamePackage.doubleWin || 0)}
              </p>
            </div>
            <div>
              <p className='text-gray-500'>3x Win</p>
              <p className='font-medium'>
                ₱{formatCurrency(gamePackage.tripleWin || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <EditDefaultGamePackage
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        refreshGames={refreshPackages}
        gamePackage={gamePackage}
      />
    </>
  );
}
